import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { ModeloGetUrl, ModeloPost } from "../../components/ModeloCrud";
import { toast } from "react-toastify";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh, PersonOff } from "@mui/icons-material";
import { COLORS } from "../SalaEspera/salaEsperaUtils";
import TicketCard from "../SalaEspera/TicketCard";
import { ArrowBack } from "@mui/icons-material";

const PISO_LABEL = {
  PB: "Planta Baja",
  "01": "1er Piso",
  "02": "2do Piso",
  "03": "3er Piso",
};

function getClinica(t) {
  return t.totem_clinica ?? "Sin clínica";
}

function getPiso(t) {
  return t.consultorio_piso ?? "PB";
}

function getTabKey(t) {
  return `${getClinica(t)}|${getPiso(t)}`;
}

function getTabCombos(tickets) {
  const seen = new Set();
  const combos = [];
  tickets.forEach((t) => {
    const key = getTabKey(t);
    if (!seen.has(key)) {
      seen.add(key);
      combos.push({ key, clinica: getClinica(t), piso: getPiso(t) });
    }
  });
  return combos.sort((a, b) => {
    const pa = a.piso === "PB" ? "0" : a.piso;
    const pb = b.piso === "PB" ? "0" : b.piso;
    return a.clinica.localeCompare(b.clinica) || pa.localeCompare(pb);
  });
}

export default function SalaEsperaPage() {
  const location = useLocation();
  const centroId = location.state?.centroId;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [llamandoId, setLlamandoId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const history = useHistory();

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = centroId ? `centro_id=${centroId}` : null;
      const result = await ModeloGetUrl(
        "tickets-atencion",
        null,
        "dia",
        params,
      );

      if (result.error === false) {
        const lista = result.listado ?? [];
        setTickets(lista);
        setActiveTab((prev) => {
          if (prev) return prev;
          const combos = getTabCombos(lista);
          return combos.length > 0 ? combos[0].key : null;
        });
      } else {
        toast.error(result.errormessage ?? "Error al cargar tickets");
      }
    } catch (err) {
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
      setLastUpdate(new Date());
    }
  };

  const handleLlamar = async (id) => {
    setLlamandoId(id);
    try {
      const result = await ModeloPost("tickets-atencion", id, "llamar", {});
      if (result.error === false) {
        toast.success("Paciente llamado");
        await fetchTickets();
      } else {
        toast.error(result.errormessage ?? "Error al llamar paciente");
      }
    } catch (err) {
      toast.error("Error de conexión");
    } finally {
      setLlamandoId(null);
    }
  };

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 30000);
    return () => clearInterval(interval);
  }, []);

  const combos = getTabCombos(tickets);

  const ticketsFiltrados = activeTab
    ? tickets.filter((t) => getTabKey(t) === activeTab)
    : [];

  const countForCombo = (key) =>
    tickets.filter((t) => getTabKey(t) === key).length;

  return (
    <Box sx={{ minHeight: "100vh", background: COLORS.pageBg }}>
      {/* ── Header fijo ── */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          px: 3,
          pt: 2,
        }}
      >
        {/* Fila superior: título + refresh + hora */}
        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <Typography variant="h5" fontWeight={700} color={COLORS.dark}>
            Salas de Espera
          </Typography>

          <Tooltip title="Actualizar">
            <span>
              <IconButton
                onClick={fetchTickets}
                disabled={loading}
                size="small"
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Refresh sx={{ fontSize: 22, color: COLORS.muted }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {lastUpdate
              ? `Actualizado ${lastUpdate.toLocaleTimeString("es-AR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "Cargando..."}
          </Typography>
        </Box>

        {/* Volver */}
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          mb={1}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.push("/home")}
        >
          <ArrowBack sx={{ fontSize: 18, color: COLORS.muted }} />
          <Typography variant="body2" color="text.secondary">
            Volver
          </Typography>
        </Box>

        {/* Tabs por clínica (totem_clinica) + piso */}
        {combos.length > 0 && (
          <Tabs
            value={activeTab ?? false}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                minHeight: 40,
                py: 0,
                textTransform: "none",
                fontSize: 13,
              },
            }}
          >
            {combos.map(({ key, clinica, piso }) => (
              <Tab
                key={key}
                value={key}
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <span>{`${clinica} · ${PISO_LABEL[piso] ?? piso}`}</span>
                    <Chip
                      label={countForCombo(key)}
                      size="small"
                      color={activeTab === key ? "primary" : "default"}
                      sx={{
                        height: 18,
                        fontSize: 11,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        )}
      </Box>

      {/* ── Contenido ── */}
      <Box sx={{ p: 3 }}>
        {/* Sin tickets en absoluto */}
        {!loading && tickets.length === 0 && (
          <Box textAlign="center" mt={8}>
            <PersonOff sx={{ fontSize: 64, color: COLORS.info, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No hay pacientes en espera hoy
            </Typography>
          </Box>
        )}

        {/* Sin tickets en el tab activo (pero hay otros) */}
        {!loading && tickets.length > 0 && ticketsFiltrados.length === 0 && (
          <Box textAlign="center" mt={8}>
            <PersonOff sx={{ fontSize: 48, color: COLORS.info, mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No hay pacientes en espera en esta sala
            </Typography>
          </Box>
        )}

        {/* Lista de tickets */}
        {ticketsFiltrados.map((t) => (
          <TicketCard
            key={t.id}
            ticket={t}
            onLlamar={handleLlamar}
            llamando={llamandoId === t.id}
          />
        ))}
      </Box>
    </Box>
  );
}
